import { useState } from 'react';
import PropTypes from 'prop-types';

import AppDialog from '#/modules/commonComponents/dialog/AppDialog';
import {
  dialogContentCompactGapClassName,
} from '#/modules/commonComponents/dialog/dialogLayoutTokens';
import FloatingDateInput from '#/modules/commonComponents/form/FloatingDateInput';
import FloatingInput from '#/modules/commonComponents/form/FloatingInput';
import { toast } from '#/utils/toast';

/**
 * 開票 Dialog：invoiceNo／invoicedAt／invoiceTitle／taxIdNumber。
 */
const InvoiceDialogSession = ({
  open,
  onOpenChange,
  isPending,
  initialValues,
  onConfirm,
}) => {
  const [invoiceNo, setInvoiceNo] = useState(initialValues?.invoiceNo ?? '');
  const [invoicedAt, setInvoicedAt] = useState(initialValues?.invoicedAt ?? '');
  const [invoiceTitle, setInvoiceTitle] = useState(initialValues?.invoiceTitle ?? '');
  const [taxIdNumber, setTaxIdNumber] = useState(initialValues?.taxIdNumber ?? '');

  function handleCancel() {
    onOpenChange(false);
  }

  function handleConfirm() {
    if (isPending) {
      return;
    }
    const trimmedNo = invoiceNo.trim();
    const trimmedAt = typeof invoicedAt === 'string' ? invoicedAt.trim() : invoicedAt;
    const trimmedTitle = invoiceTitle.trim();
    const trimmedTaxId = taxIdNumber.trim();
    if (!trimmedNo) {
      toast.error('請填寫發票號碼');
      return;
    }
    if (!trimmedAt) {
      toast.error('請填寫開票日期');
      return;
    }
    if (!trimmedTitle) {
      toast.error('請填寫發票抬頭');
      return;
    }
    if (!trimmedTaxId) {
      toast.error('請填寫統一編號');
      return;
    }
    onConfirm?.({
      invoiceNo: trimmedNo,
      invoicedAt: trimmedAt,
      invoiceTitle: trimmedTitle,
      taxIdNumber: trimmedTaxId,
    });
  }

  const canConfirm = Boolean(
    invoiceNo.trim()
    && (typeof invoicedAt === 'string' ? invoicedAt.trim() : invoicedAt)
    && invoiceTitle.trim()
    && taxIdNumber.trim(),
  );

  return (
    <AppDialog
      open={open}
      onOpenChange={onOpenChange}
      title="開票"
      description="需填發票號碼、開票日、抬頭與統編。已開票狀態可再送出以修正上述欄位。"
      headerVariant="muted"
      contentClassName={dialogContentCompactGapClassName}
      closeOnOutsideClick={false}
      onCancel={handleCancel}
      onConfirm={handleConfirm}
      confirmText={isPending ? '開票中…' : '確認'}
      confirmDisabled={!canConfirm || isPending}
      cancelText="取消"
    >
      <div className="space-y-3 pt-2.5">
        <FloatingInput
          id="sales-order-invoice-no"
          label="發票號碼"
          value={invoiceNo}
          onChange={(event) => setInvoiceNo(event.target.value)}
        />
        <FloatingDateInput
          id="sales-order-invoiced-at"
          label="開票日期"
          value={invoicedAt}
          onChange={setInvoicedAt}
          placeholder="西元年/月/日"
        />
        <FloatingInput
          id="sales-order-invoice-title"
          label="發票抬頭"
          value={invoiceTitle}
          onChange={(event) => setInvoiceTitle(event.target.value)}
        />
        <FloatingInput
          id="sales-order-tax-id-number"
          label="統一編號"
          value={taxIdNumber}
          onChange={(event) => setTaxIdNumber(event.target.value)}
        />
      </div>
    </AppDialog>
  );
};

InvoiceDialogSession.propTypes = {
  open: PropTypes.bool.isRequired,
  onOpenChange: PropTypes.func.isRequired,
  isPending: PropTypes.bool,
  initialValues: PropTypes.shape({
    invoiceNo: PropTypes.string,
    invoicedAt: PropTypes.string,
    invoiceTitle: PropTypes.string,
    taxIdNumber: PropTypes.string,
  }),
  onConfirm: PropTypes.func,
};

const SalesOrderInvoiceDialog = ({
  open,
  onOpenChange,
  isPending = false,
  initialValues,
  onConfirm,
}) => {
  if (!open) {
    return (
      <AppDialog
        open={false}
        onOpenChange={onOpenChange}
        title="開票"
        description="需填發票號碼、開票日、抬頭與統編。"
        headerVariant="muted"
      />
    );
  }

  return (
    <InvoiceDialogSession
      open={open}
      onOpenChange={onOpenChange}
      isPending={isPending}
      initialValues={initialValues}
      onConfirm={onConfirm}
    />
  );
};

SalesOrderInvoiceDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  onOpenChange: PropTypes.func.isRequired,
  isPending: PropTypes.bool,
  initialValues: PropTypes.shape({
    invoiceNo: PropTypes.string,
    invoicedAt: PropTypes.string,
    invoiceTitle: PropTypes.string,
    taxIdNumber: PropTypes.string,
  }),
  onConfirm: PropTypes.func,
};

export default SalesOrderInvoiceDialog;
